import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  SvgIcon,
  SxProps,
  Theme
} from '@mui/material';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { borderRadius, boxShadows, palette, spaces, typography } from '../../../../assets/styles/themes/tokens';
import getMuiIcon, { IconName } from '../../../../utils/getMuiIcon';
import CustomMuiCardActions from './CustomMuiCardActions';
import { MenuItemProps } from './CustomMuiCardActions/CustomMuiCardActions';
interface CustomMuiCardProps {
  title: string;
  avatar?: IconName;
  subheader?: string;
  menuItems?: MenuItemProps[];
  color?: string;
  onClick?: () => void;
  sxCardStyle?: {
    main: SxProps<Theme>;
    title?: SxProps<Theme>;
    header?: SxProps<Theme>;
    content?: SxProps<Theme>;
    banner?: SxProps<Theme>;
    avatar?: SxProps<Theme>;
    subheader?: SxProps<Theme>;
  };
  onClickBackButton?: () => void;
  children?: React.ReactNode;
  banner?: React.JSX.Element;
  action?: React.JSX.Element;
}

const CustomMuiCard: React.FC<CustomMuiCardProps> = (props) => {
  const { t } = useTranslation();
  const {
    title, 
    subheader, 
    children,
    avatar,
    menuItems = [],
    color = palette.text.primary,
    onClick,
    onClickBackButton,
    sxCardStyle,
    banner,
    action,
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  return (
    <Card
      tabIndex={0}
      onClick={isMenuOpen ? undefined : onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
        borderRadius: borderRadius.md,
        border: `1px solid ${palette.elevation.outlined}`,
        boxShadow: boxShadows.elevation1,
        backgroundColor: palette.background.paperElevation2,
        ...sxCardStyle?.main,
      }}
    >
      {
        banner && <Box sx={{  ...sxCardStyle?.banner }}>
          {banner}
        </Box>
      }
      <CardHeader
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          padding: spaces.md,
          '& .MuiCardHeader-avatar': { marginRight: spaces.xs },
          '& .MuiCardHeader-action': { margin: 0 },
          ...sxCardStyle?.header
        }}
        {...((avatar || onClickBackButton) && {
          avatar: (
            <Box display='flex' alignItems='center' sx={{ ...sxCardStyle?.avatar }}>
              {onClickBackButton && <IconButton
                data-testid={'custom-card-back-button'}
                color='secondary'
                size='small'
                onClick={onClickBackButton}
                sx={{ marginRight: spaces.md }}
                aria-label={t('Back')}
              >
                <SvgIcon
                  sx={{
                    width: 24,
                    height: 24,
                    color: palette.text.primary,
                  }}
                  component={getMuiIcon('arrowLeft')}
                />
              </IconButton>}
              {avatar && <SvgIcon
                data-testid='avatar-icon'
                sx={{
                  width: 24,
                  height: 32,
                  fill: color,
                }}
                component={getMuiIcon(avatar)}
              />}
            </Box>
          )
        })}
        {...((menuItems?.length > 0 || action) && {
          action: (
            action ? action :
              <CustomMuiCardActions
                menuItems={menuItems}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                isMenuOpen={isMenuOpen}
              />
          ),
        })}
        title={title}
        titleTypographyProps={{
          sx: {
            ...(typography.formLabel),
            ...sxCardStyle?.title
          },
        }}
        subheaderTypographyProps={{
          sx: {
            ...sxCardStyle?.subheader
          },
        }}
        {...(subheader && { subheader })}
      />
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: spaces.md,
          gap: spaces.md,
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: `${palette.elevation.outlined} transparent`,
          flexWrap: 'nowrap',
          width: '100%',
          scrollBehavior: 'smooth',
          '&:last-child': { paddingBottom: spaces.md },
          ...sxCardStyle?.content
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
};

export default memo(CustomMuiCard);
